import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { adminQuoteQuery } from "@/lib/corporate/queries";
import { adminConvertQuoteFn, adminUpdateQuoteFn } from "@/lib/corporate/corporate.functions";
import { formatDateTime, formatPaise } from "@/lib/corporate/format";
import { QUOTE_STATUS_LABEL, QUOTE_TRANSITIONS, type CorporateQuoteStatus } from "@/lib/corporate/types";

export const Route = createFileRoute("/admin/quotes/$id")({
  component: () => (
    <RequirePermission perm={["orders.read", "orders.write"]}><QuoteDetailPage /></RequirePermission>
  ),
});

type ItemDraft = { id?: string; packSlug: string; packName: string; variantLabel: string; qty: number; unitPriceCents: number; notes: string | null; sortOrder: number };

const FIELD = "w-full border border-black/15 px-2 py-1.5 text-sm";

function QuoteDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery(adminQuoteQuery(id));
  const quote = data?.quote ?? null;

  const [items, setItems] = useState<ItemDraft[]>([]);
  const [notes, setNotes] = useState("");
  const [rep, setRep] = useState("");
  const [po, setPo] = useState("");
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [tax, setTax] = useState(0);
  const [validUntil, setValidUntil] = useState("");

  useEffect(() => {
    if (!quote) return;
    setItems(quote.items.map((i) => ({
      id: i.id, packSlug: i.packSlug, packName: i.packName, variantLabel: i.variantLabel,
      qty: i.qty, unitPriceCents: i.unitPriceCents, notes: i.notes, sortOrder: i.sortOrder,
    })));
    setNotes(quote.internalNotes ?? "");
    setRep(quote.salesRepEmail ?? "");
    setPo(quote.poNumber ?? "");
    setDiscount(quote.discountCents);
    setShipping(quote.shippingCents);
    setTax(quote.taxCents);
    setValidUntil(quote.validUntil ? quote.validUntil.slice(0, 10) : "");
  }, [quote]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "corporate"] });
  };

  const save = useMutation({
    mutationFn: (status?: CorporateQuoteStatus) =>
      adminUpdateQuoteFn({
        data: {
          id,
          ...(status ? { status } : {}),
          internalNotes: notes || null,
          salesRepEmail: rep || null,
          poNumber: po || null,
          discountCents: discount,
          shippingCents: shipping,
          taxCents: tax,
          validUntil: validUntil ? new Date(validUntil).toISOString() : null,
          items,
        },
      }),
    onSuccess: (res) => {
      if (res.error) toast.error(res.error);
      else { toast.success("Quote saved"); invalidate(); }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const convert = useMutation({
    mutationFn: () => adminConvertQuoteFn({ data: { quoteId: id } }),
    onSuccess: (res) => {
      if (res.error) toast.error(res.error);
      else {
        toast.success(`Order ${res.orderNumber} created`);
        invalidate();
        void navigate({ to: "/admin/orders" });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <p className="text-sm text-black/60">Loading quote…</p>;
  if (error) return <p className="text-sm text-red-700">{(error as Error).message}</p>;
  if (!quote) return <p className="text-sm text-black/60">Quote not found.</p>;

  const subtotal = items.reduce((s, i) => s + i.qty * i.unitPriceCents, 0);
  const total = Math.max(0, subtotal - discount) + shipping + tax;
  const nextStates = QUOTE_TRANSITIONS[quote.status] ?? [];
  const busy = save.isPending || convert.isPending;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/quotes" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/50 hover:text-[#C6A15B]">← All quotes</Link>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">{quote.quoteNumber}</h2>
          <p className="mt-1 text-sm text-black/55">
            {quote.companyName} · {quote.contactName} ({quote.contactEmail}) · {QUOTE_STATUS_LABEL[quote.status]}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => save.mutate(undefined)} className="border border-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5 disabled:opacity-50">Save</button>
          {nextStates.map((s) => (
            <button key={s} type="button" disabled={busy} onClick={() => save.mutate(s)} className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-black/85 disabled:opacity-50">
              Mark {QUOTE_STATUS_LABEL[s]}
            </button>
          ))}
          {quote.status === "approved" && (
            <button type="button" disabled={busy} onClick={() => convert.mutate()} className="border border-emerald-700 bg-emerald-700 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-50">
              Convert to order
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <section className="rounded-sm border border-black/10 bg-white p-4">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Quote builder</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="text-[10px] uppercase tracking-[0.18em] text-black/45">
                <tr><th className="py-2">Pack</th><th className="py-2">Variant</th><th className="py-2 w-24">Qty</th><th className="py-2 w-36">Unit (paise)</th><th className="py-2 text-right">Line</th><th /></tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id ?? `new-${idx}`} className="border-t border-black/5">
                    <td className="py-2 pr-2"><input className={FIELD} value={it.packName} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, packName: e.target.value } : x))} /></td>
                    <td className="py-2 pr-2"><input className={FIELD} value={it.variantLabel} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, variantLabel: e.target.value } : x))} /></td>
                    <td className="py-2 pr-2"><input type="number" min={1} className={FIELD} value={it.qty} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, qty: Number(e.target.value) } : x))} /></td>
                    <td className="py-2 pr-2"><input type="number" min={0} className={FIELD} value={it.unitPriceCents} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { ...x, unitPriceCents: Number(e.target.value) } : x))} /></td>
                    <td className="py-2 text-right">{formatPaise(it.qty * it.unitPriceCents)}</td>
                    <td className="py-2 pl-2 text-right">
                      <button type="button" className="text-[11px] uppercase tracking-[0.16em] text-red-700" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="mt-3 border border-black/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] hover:bg-black/5"
            onClick={() => setItems((p) => [...p, { packSlug: "", packName: "", variantLabel: "", qty: 1, unitPriceCents: 0, notes: null, sortOrder: p.length }])}
          >
            Add line
          </button>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <Num label="Discount (paise)" value={discount} onChange={setDiscount} />
            <Num label="Shipping (paise)" value={shipping} onChange={setShipping} />
            <Num label="GST (paise)" value={tax} onChange={setTax} />
          </div>

          <dl className="mt-5 ml-auto max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-black/55">Subtotal</dt><dd>{formatPaise(subtotal)}</dd></div>
            <div className="flex justify-between border-t border-black/10 pt-2 text-base font-semibold"><dt>Total</dt><dd>{formatPaise(total)}</dd></div>
          </dl>
        </section>

        <div className="flex flex-col gap-5">
          <section className="rounded-sm border border-black/10 bg-white p-4">
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Assignment & terms</h3>
            <div className="mt-3 space-y-3">
              <Text label="Sales representative (email)" value={rep} onChange={setRep} />
              <Text label="Purchase order number" value={po} onChange={setPo} />
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Valid until</label>
                <input type="date" className={FIELD} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Internal notes</label>
                <textarea rows={4} className={FIELD} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          </section>

          {quote.shippingAddresses.length > 0 && (
            <section className="rounded-sm border border-black/10 bg-white p-4">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Delivery addresses</h3>
              <ul className="mt-3 space-y-2 text-xs text-black/65">
                {quote.shippingAddresses.map((s, i) => (
                  <li key={i} className="border border-black/10 p-2">
                    <strong className="block text-black">{s.label || s.recipientName}</strong>
                    {s.address.line1}, {s.address.city} {s.address.postalCode}
                    {s.brandingNotes && <span className="mt-1 block text-[#8a6d3b]">Branding: {s.brandingNotes}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-sm border border-black/10 bg-white p-4">
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Timeline</h3>
            <ol className="mt-3 space-y-2">
              {quote.events.map((e) => (
                <li key={e.id} className="border-l-2 border-[#C6A15B]/50 pl-3 text-xs text-black/60">
                  <span className="font-medium text-black">{e.type}</span> · {formatDateTime(e.createdAt)}
                  {e.actorEmail && <span className="block text-black/45">{e.actorEmail}</span>}
                  {e.message && <span className="block">{e.message}</span>}
                </li>
              ))}
              {quote.events.length === 0 && <li className="text-xs text-black/45">No activity yet.</li>}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}

function Text({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">{label}</label>
      <input className={FIELD} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Num({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">{label}</label>
      <input type="number" min={0} className={FIELD} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}